<script setup>
import copyIcon from '@/assets/images/copy-icon.svg'
import mediaVideoIcon from '@/assets/images/media-video-icon.svg'
import { hasDirectDocumentDownload } from '../composables/useContentLibraryDocumentDownloadActions'

const props = defineProps({
  item: {
    type: Object,
    required: true
  },
  elementId: {
    type: String,
    default: ''
  },
  title: {
    type: String,
    default: 'Untitled item'
  },
  description: {
    type: String,
    default: ''
  },
  documentActionLabel: {
    type: String,
    default: 'Download File'
  },
  documentActionIcon: {
    type: String,
    default: 'bi-download'
  },
  documentActions: {
    type: Array,
    default: () => []
  },
  actionLabel: {
    type: String,
    default: ''
  },
  actionIcon: {
    type: String,
    default: 'bi-arrow-right-circle'
  },
  showFavoriteAction: {
    type: Boolean,
    default: true
  },
  favoriteActionLabel: {
    type: String,
    default: 'Add to Favorites'
  },
  isFavorite: {
    type: Boolean,
    default: false
  },
  isFavoriteActionLoading: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['copy-password', 'download-document', 'run-action', 'toggle-favorite'])

const handleCopyPassword = () => {
  emit('copy-password', props.item.zoomPassword)
}

const handleDocumentDownload = () => {
  emit('download-document', props.item)
}

const handleAction = () => {
  emit('run-action', props.item)
}

const handleDocumentActionDownload = (action) => {
  emit('download-document', { item: props.item, action })
}

const handleToggleFavorite = () => {
  emit('toggle-favorite', props.item)
}
</script>

<template>
  <div :id="elementId" class="content-library-item-card">
    <div class="content-library-item-card-header">
      <p class="content-library-item-title text-16">{{ title }}</p>
    </div>

    <div class="content-library-item-card-body">
      <div class="content-library-item-copy">
        <p v-if="description" class="content-library-item-description text-14">
          {{ description }}
        </p>
        <p v-else class="content-library-item-description text-14 content-library-item-description-empty">
          No description available for this content item.
        </p>

        <div v-if="item.zoomPassword" class="content-library-item-zoom-password text-14">
          <span class="content-library-item-zoom-password-label">Password: {{ item.zoomPassword }}</span>
          <button
            type="button"
            class="content-library-item-copy-button"
            @click="handleCopyPassword"
          >
            <img :src="copyIcon" alt="" class="content-library-item-copy-icon me-1" />
            <span>Copy to Clipboard</span>
          </button>
        </div>
      </div>

      <div class="content-library-item-actions">
        <a
          v-if="actionLabel"
          href="#"
          @click.prevent="handleAction"
          class="content-library-item-action-link"
        >
          <i class="bi me-2" :class="actionIcon"></i>
          <span>{{ actionLabel }}</span>
        </a>
        <a
          v-if="item.zoomUrl"
          :href="item.zoomUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="content-library-item-action-link"
        >
          <img :src="mediaVideoIcon" alt="" class="content-library-item-action-icon me-2" />
          <span class="content-library-item-action-label content-library-item-action-label--video">Go to Video Page</span>
        </a>
        <a
          v-for="action in documentActions"
          :key="action.key"
          href="#"
          @click.prevent="handleDocumentActionDownload(action)"
          class="content-library-item-action-link"
        >
          <i class="bi me-2" :class="action.icon"></i>
          <span>{{ action.label }}</span>
        </a>
        <a
          v-if="!documentActions.length && hasDirectDocumentDownload(item)"
          href="#"
          @click.prevent="handleDocumentDownload"
          class="content-library-item-action-link"
        >
          <i class="bi me-2" :class="documentActionIcon"></i>
          <span>{{ documentActionLabel }}</span>
        </a>
        <button
          v-if="showFavoriteAction"
          type="button"
          class="content-library-item-action-link content-library-item-action-button"
          :class="{ 'is-active': isFavorite }"
          :disabled="isFavoriteActionLoading"
          @click="handleToggleFavorite"
        >
          <i
            class="bi me-2"
            :class="isFavorite ? 'bi-star-fill' : 'bi-star'"
          ></i>
          <span>{{ favoriteActionLabel }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.content-library-item-card {
  border: 1px solid var(--bs-gray-300);
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.05);
}

.content-library-item-card-header {
  padding: 12px 18px;
  background: linear-gradient(90deg, #f3eefc 0%, #f8f4ff 100%);
  border-bottom: 1px solid rgba(139, 92, 246, 0.14);
}

.content-library-item-card-body {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  padding: 18px;
}

.content-library-item-title {
  margin: 0;
  font-weight: 700 !important;
  color: var(--bs-gray-900);
}

.content-library-item-description {
  margin: 0;
  color: var(--bs-gray-700);
  line-height: 1.5;
}

.content-library-item-description-empty {
  color: var(--bs-gray-500);
}

.content-library-item-copy {
  flex: 1 1 auto;
  min-width: 0;
}

.content-library-item-zoom-password {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 12px;
}

.content-library-item-zoom-password-label {
  color: var(--bs-gray-900);
  font-weight: 600;
}

.content-library-item-copy-button {
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--bs-primary);
  font-weight: 600;
}

.content-library-item-copy-button:hover,
.content-library-item-copy-button:focus {
  color: var(--bs-primary);
  text-decoration: underline;
}

.content-library-item-copy-icon {
  width: 16px;
  height: 16px;
  flex: 0 0 16px;
  object-fit: contain;
}

.content-library-item-action-icon {
  width: 18px;
  height: 18px;
  flex: 0 0 18px;
  object-fit: contain;
}

.content-library-item-action-label {
  display: inline-flex;
  align-items: center;
}

.content-library-item-action-label--video {
  transform: translateY(2px);
}

.content-library-item-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  min-width: 220px;
  padding-left: 0;
  margin-top: -10px;
  border-left: 1px solid var(--bs-gray-200);
}

.content-library-item-action-link {
  display: inline-flex;
  align-items: center;
  color: var(--bs-primary);
  font-weight: 600;
  text-decoration: none;
  line-height: 1.3;
}

.content-library-item-action-link:hover {
  color: var(--bs-primary);
  text-decoration: underline;
}

.content-library-item-action-link:focus,
.content-library-item-action-link:focus-visible,
.content-library-item-action-link:active,
.content-library-item-action-link:visited {
  color: var(--bs-primary);
  text-decoration: none;
}

.content-library-item-action-link i {
  font-size: 18px;
}

.content-library-item-action-button {
  padding: 0;
  border: none;
  background: transparent;
}

.content-library-item-action-button.is-active {
  color: #6f42c1;
}

.content-library-item-action-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .content-library-item-card-body {
    flex-direction: column;
    gap: 16px;
  }

  .content-library-item-actions {
    min-width: 0;
    width: 100%;
    padding-left: 0;
    padding-top: 14px;
    border-left: none;
    border-top: 1px solid var(--bs-gray-200);
  }
}
</style>
